import { Paths } from "@app/routes";
import { useToast } from "@atlas/core";
import { useSessionData, useTranslation } from "@atlas/sdk";
import { HubPackageStatus, type IHubPackageVisibility } from "@enums";
import type { IHubPackage } from "@models";
import { useCreateHubPackage, useHubCurrentCart, useUserInfo } from "@query";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useSwitchPackage } from "./useSwitchPackage";

export function useCreatePackageSave(
  setCreatePackageOpen: (open: boolean) => void,
  options?: { onSuccess?: (data: IHubPackage) => void },
) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { mutate: createHubPackage, isPending: isCreatePending } =
    useCreateHubPackage();

  const { data: userInfo } = useUserInfo();
  const companyId = userInfo?.companyId ?? 0;

  const { userId } = useSessionData();
  const uid = (userId ?? "").trim();
  const { data: userHubCart } = useHubCurrentCart(uid, {
    enabled: uid.length > 0,
  });

  const { switchPackage, isSwitchPending } = useSwitchPackage(userHubCart);

  const handleCreatePackageSave = useCallback(
    (values: {
      label: string;
      description: string;
      visibility: IHubPackageVisibility;
    }) => {
      const body: IHubPackage = {
        companyId,
        name: values.label,
        label: values.label,
        description: values.description,
        status: HubPackageStatus.Ready,
        catalogRelations: [],
        visibility: values.visibility,
      };

      createHubPackage(body, {
        onSuccess: (data: IHubPackage) => {
          setCreatePackageOpen(false);
          toast.add({
            title: t("hub.package_manager.create_success", {
              defaultValue: "Package created",
            }),
            data: { type: "success" },
          });
          if (data?.packageId) {
            switchPackage(data, {
              onSuccess: () => {
                options?.onSuccess?.(data);
                navigate(`/${Paths.CatalogManager}`);
              },
              onError: () => {
                options?.onSuccess?.(data); // Still call onSuccess for creation even if switch fails
                navigate(`/${Paths.CatalogManager}`);
              },
            });
          } else {
            options?.onSuccess?.(data);
            navigate(`/${Paths.CatalogManager}`);
          }
        },
        onError: (error: unknown) => {
          const apiError = error as { status?: number; message?: string };
          toast.add({
            title: t("hub.package_manager.create_error", {
              defaultValue: "Could not create package",
            }),
            ...(apiError.status === 409 && apiError.message
              ? { description: apiError.message }
              : {}),
            data: { type: "error" },
          });
        },
      });
    },
    [
      createHubPackage,
      switchPackage,
      t,
      toast,
      setCreatePackageOpen,
      navigate,
      options,
      companyId,
    ],
  );

  return {
    handleCreatePackageSave,
    isCreatePending: isCreatePending || isSwitchPending,
  };
}
